<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-surface-raised border-b border-border px-6 py-4">
            <div class="max-w-3xl mx-auto">
                <BasePageTitle no-border title="Senior Leader Self-Assessment" subtitle="Take a few minutes to reflect on your leadership behaviors across 5 key competencies." />
            </div>
        </div>

        <div class="flex-1 overflow-y-auto">
            <Transition name="fade" mode="out-in">
                <div v-if="phase === 'disclaimer'" key="disclaimer" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-shield-check text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <!-- FIXME: Consider extracting assessment section headers to a shared BasePageHeader component -->
                            <h2 class="title-body-bold text-ink">Assessment Disclaimer</h2>
                        </div>
                        <p class="body text-ink mb-4">
                            The CVS Health Leadership Competencies set a consistent, elevated standard for how we lead and develop leadership capability across our company. Our
                            five leadership competencies are People-First Leadership, Drives Performance, Agile Learner, Champions AI, and Shapes the Future. This self-assessment
                            is designed to help you identify areas of strength and opportunities to grow.
                        </p>
                        <p class="body text-ink mb-4">
                            The goal of this self-assessment is to promote development. This is <strong>not</strong> intended to be a rating or evaluation of your current
                            performance in your role and is separate from the performance management process. Individual and aggregate data will be available to only a limited
                            group of HR colleagues to inform future assessments and development opportunities related to the competencies.
                        </p>
                        <p class="body text-ink mb-4">
                            Please respond as candidly as you can based on your professional experiences. Your reflections will help you to identify strengths to build on and to
                            identify some focused growth areas.
                        </p>
                        <p class="body text-ink">If you are unsure on an item, please choose the best response based on your typical behavior.</p>
                    </div>
                </div>

                <div v-else-if="phase === 'instructions'" key="instructions" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-list-check text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-ink">How This Works</h2>
                        </div>
                        <p class="body text-ink mb-4">
                            This assessment contains <strong>20 different behaviors</strong> and definitions that you will self-assess. For each question, please consider how this
                            behavior appears — or does not presently appear — in your daily work.
                        </p>
                        <p class="body text-ink mb-4">
                            There is a rating scale from <strong>1–5</strong> for each behavior. You may also mark a behavior as <strong>N/A</strong> if it does not apply to your
                            role.
                        </p>
                        <p class="body text-ink mb-4">
                            Please remember that this is <strong>not a performance evaluation</strong>, but rather a tool to assist in your ongoing development.
                        </p>
                        <p class="body text-ink mb-4">
                            An example competency, behavior, definition, and rating scale will be presented to you next so that you can orient yourself before beginning the
                            assessment.
                        </p>
                        <p class="body text-ink-muted">
                            Once you complete the assessment, you will be given a choice to download your summary or to start coaching with Nadia to take your self-reflection
                            further.
                        </p>
                    </div>
                </div>

                <div v-else-if="phase === 'example'" key="example" class="max-w-3xl mx-auto px-6 py-10 space-y-5">
                    <div class="bg-surface-raised rounded-2xl border border-border shadow-sm p-10">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-surface-sunken flex items-center justify-center shrink-0">
                                <i class="bi bi-eye text-ink text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-ink">Example Question</h2>
                        </div>
                        <p class="body-small text-ink-muted mb-6">
                            You will be presented a series of behaviors and statements on which you will be asked to assess yourself. Here is what a question looks like:
                        </p>
                        <div class="bg-surface-sunken rounded-xl border border-border p-5 mb-6">
                            <div class="flex items-start justify-between gap-4 mb-3">
                                <div class="flex-1">
                                    <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                                    <p class="details font-bold tracking-widest text-ink uppercase mb-1">People-First Leadership</p>
                                    <h3 class="body-bold text-ink">Sets performance expectations</h3>
                                    <p class="body-small text-ink-muted mt-1">
                                        Communicates high performance expectations linked to business goals; checks for understanding of and commitment to goals.
                                    </p>
                                </div>
                                <div class="shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-border text-ink-muted">
                                    <span class="title-body-bold leading-none">N/A</span>
                                </div>
                            </div>
                            <div class="mt-3">
                                <div class="h-1 rounded-full bg-border w-full mb-2"></div>
                                <div class="flex justify-between px-[10px]">
                                    <span class="text-[10px] text-ink-muted font-semibold">N/A</span>
                                    <span v-for="n in 5" :key="n" class="text-[10px] text-ink-muted font-semibold">{{ n }} · {{ rating_labels[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                        <p class="body-small-bold text-ink mb-3">Rating scale (applies to all questions):</p>
                        <div class="divide-y divide-border">
                            <div class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-surface-sunken text-ink-muted details flex items-center justify-center">N/A</div>
                                <div>
                                    <span class="body-small-bold text-ink">Behavior not yet applicable</span>
                                    <span class="body-small text-ink-muted"> — This behavior doesn't currently apply to my role.</span>
                                </div>
                            </div>
                            <div v-for="n in 5" :key="n" class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-surface-sunken text-ink details flex items-center justify-center">
                                    {{ n }}
                                </div>
                                <div>
                                    <span class="body-small-bold text-ink">{{ rating_labels[String(n)] }}</span>
                                    <span class="body-small text-ink-muted"> — {{ rating_descriptions[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>

        <div class="shrink-0 bg-surface-raised border-t border-border px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <BaseButton v-if="phase !== 'disclaimer'" theme="tertiary" :disabled="isStartingAssessment" @click="emit('intro-prev')"> ← Back </BaseButton>
                <div v-else></div>
                <BaseButton theme="primary" :disabled="isStartingAssessment" @click="emit('intro-next')">
                    {{ phase === "example" ? (isStartingAssessment ? "Starting..." : "Start Assessment →") : "Continue →" }}
                </BaseButton>
            </div>
            <div v-if="startError" role="alert" class="max-w-3xl mx-auto mt-2 details text-notification-warning-text">
                {{ startError }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";

type IntroPhase = "disclaimer" | "instructions" | "example";

interface Props {
    phase: IntroPhase;
    isStartingAssessment: boolean;
    startError: string | null;
    rating_labels: Record<string, string>;
    rating_descriptions: Record<string, string>;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: "intro-prev"): void;
    (e: "intro-next"): void;
}>();
</script>
